import styles from "./Lojas.module.scss";

import LojaItem from "shared/components/lojaitem/LojaItem";

import { useEffect, useMemo, useState } from "react";

import { ILoja } from "shared/interfaces/ILoja";

import { buscarLojasPorUsuario } from "shared/services/api/lojas.api";

import { formatarNumero } from "shared/utils/formatarNumero";

import { useSearchParams } from "react-router-dom";

import { ValidarContaModal } from "shared/components/modal/ValidarContaModal";

import { gerarLinkKyc } from "shared/services/api/dadosBancarios.api";

interface LojasProps {
  ordem?: "asc" | "desc";
  filtroAtivo?: boolean;
}

export const Lojas: React.FC<LojasProps> = ({
  ordem = "asc",
  filtroAtivo = false,
}) => {
  const [lojas, setLojas] = useState<ILoja[]>([]);

  const [loading, setLoading] = useState(true);

  // Loja que está tendo o KYC validado
  const [lojaKyc, setLojaKyc] =
    useState<ILoja | null>(null);

  const [searchParams] = useSearchParams();

  const busca =
    searchParams.get("search")?.toLowerCase() || "";

  /* =========================================
     CARREGAR LOJAS
  ========================================= */

  const carregarLojas = async () => {
    try {
      setLoading(true);

      const lojasData =
        await buscarLojasPorUsuario();

      setLojas(lojasData);
    } catch (error) {
      console.error(
        "Erro ao carregar lojas:",
        error
      );

      setLojas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarLojas();
  }, []);

  /* =========================================
     ABRIR KYC
  ========================================= */

  const handleValidarConta = (
    loja: ILoja
  ) => {
    setLojaKyc(loja);
  };

  /* =========================================
     FECHAR KYC
  ========================================= */

  const handleFecharKyc = async () => {
    setLojaKyc(null);

    // Atualiza os status das lojas
    // depois da validação
    await carregarLojas();
  };

  /* =========================================
     GERAR LINK KYC DA LOJA
  ========================================= */

  const handleGerarLinkKyc = async () => {
    if (!lojaKyc?.id) {
      throw new Error(
        "Nenhuma loja selecionada para validação."
      );
    }

    return gerarLinkKyc(
      lojaKyc.id
    );
  };

  /* =========================================
     BUSCAR + FILTRAR + ORDENAR
  ========================================= */

  const lojasProcessadas = useMemo(() => {
    let resultado = [...lojas];

    /* =====================================
       BUSCA
    ===================================== */

    if (busca) {
      resultado = resultado.filter(
        (loja) =>
          (loja.loja || "")
            .toLowerCase()
            .includes(busca)
      );
    }

    /* =====================================
       FILTRO
    ===================================== */

    if (filtroAtivo) {
      // Filtros serão adicionados aqui.
    }

    /* =====================================
       ORDENAÇÃO
    ===================================== */

    resultado.sort((a, b) => {
      const nomeA =
        (a.loja || "").toLowerCase();

      const nomeB =
        (b.loja || "").toLowerCase();

      return ordem === "asc"
        ? nomeA.localeCompare(nomeB)
        : nomeB.localeCompare(nomeA);
    });

    return resultado;
  }, [
    lojas,
    busca,
    ordem,
    filtroAtivo,
  ]);

  /* =========================================
     RENDER
  ========================================= */

  return (
    <div className={styles.page}>
      <div className={styles.listarLojas}>

        {/* =================================
            LOADING
        ================================= */}

        {loading && (
          <p>
            Carregando lojas...
          </p>
        )}

        {/* =================================
            SEM LOJAS
        ================================= */}

        {!loading &&
          lojas.length === 0 && (
            <div
              className={styles.page}
            >
              <div
                className={
                  styles.containerImg
                }
              >
                <img
                  src="/imagens/semloja.png"
                  alt="Nenhuma loja cadastrada"
                />
              </div>

              <h2>
                Nenhuma Loja Cadastrada
              </h2>
            </div>
          )}

        {/* =================================
            SEM RESULTADO DA BUSCA
        ================================= */}

        {!loading &&
          lojas.length > 0 &&
          lojasProcessadas.length === 0 && (
            <div
              className={styles.page}
            >
              <h2>
                Nenhuma loja encontrada
              </h2>
            </div>
          )}

        {/* =================================
            LISTA DE LOJAS
        ================================= */}

        {!loading &&
          lojasProcessadas.map(
            (loja, index) => (
              <LojaItem
                key={loja.id}
                loja={loja}
                numeroUI={formatarNumero(
                  index + 1
                )}
                onAtualizar={
                  carregarLojas
                }
                onValidarConta={() =>
                  handleValidarConta(
                    loja
                  )
                }
              />
            )
          )}
      </div>

      {/* =========================================
          MODAL KYC
      ========================================= */}

      {lojaKyc && (
        <ValidarContaModal
          onClose={handleFecharKyc}
          gerarLinkKyc={
            handleGerarLinkKyc
          }
        />
      )}
    </div>
  );
};